import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import dayjs from 'dayjs';
import { DatePicker, TimePicker } from 'antd';
import { CustomSpinner, Small } from '../../../pages';
import { checkServiceAreAvailableInBlockTime } from '../../../services';
import { toast } from 'react-toastify';
import moment from 'moment';
import DeleteConfirmationModal from '../../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import { Locale } from '../../../pages/Locale/Locale';

const format = 'HH:mm';


const BlockTimeModal = ({ showModal, setShowModal, openingHourseFormValue, setOpeningHourseFormValue, selectedBlockSlots, setSelectedBlockSlots }) => {
    const { t } = Locale();
    const initialFormValue = {
        date: null,
        start_time: null,
        end_time: null,
    }
    const [formValue, setFormValue] = useState(initialFormValue);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [warningModal, setWarningModal] = useState(false);

    useEffect(() => {
        if (selectedBlockSlots) {
            setFormValue({
                date: dayjs(selectedBlockSlots.date, 'DD/MM/YYYY'),
                start_time: dayjs(selectedBlockSlots.start_time, 'HH:mm'),
                end_time: dayjs(selectedBlockSlots.end_time, 'HH:mm'),
            });
        }
    }, [selectedBlockSlots]);

    const validate = (field, value) => {
        const updated = { ...formValue, [field]: value };
        const newErrors = {};

        if (!updated.date) newErrors.date = `${t('field.pleaseEnter')} ${t('field.date')}`;
        if (!updated.start_time) newErrors.start_time = `${t('field.pleaseEnterSmall')} ${t('field.startTime')}`;
        if (!updated.end_time) newErrors.end_time = `${t('field.pleaseEnterSmall')} ${t('field.endTime')}`;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (field, value) => {
        let message = '';
        if (!value) {
            if (field === 'date') message = `${t('field.pleaseEnterSmall')} ${t('field.date')}`;
            if (field === 'start_time') message = `${t('field.pleaseEnterSmall')} ${t('field.startTime')}`;
            if (field === 'end_time') message = `${t('field.pleaseEnterSmall')} ${t('field.endTime')}`;
        }
        setErrors(prev => ({ ...prev, [field]: message }));
    };


    const handleChange = (field, value) => {
        setFormValue(prev => {
            const updated = { ...prev, [field]: value };
            validateField(field, value);
            return updated;
        });
    };


    const handleSubmit = async () => {
        if (!validate()) return;

        try {

            setIsLoading(true)
            const formattedData = {
                date: dayjs(formValue.date).format('DD/MM/YYYY'),
                start_time: dayjs(formValue.start_time).format('HH:mm'),
                end_time: dayjs(formValue.end_time).format('HH:mm'),
            };

            const res = await checkServiceAreAvailableInBlockTime(formattedData);
            if (res?.data?.conflict) {
                setShowModal(false)
                setWarningModal(true)
            }
            else {
                setOpeningHourseFormValue((prev) => {
                    const updatedSlots = prev?.blockTime || [];

                    if (selectedBlockSlots) {
                        const updated = updatedSlots.map((slot) =>
                            slot.date === selectedBlockSlots.date &&
                                slot.start_time === selectedBlockSlots.start_time &&
                                slot.end_time === selectedBlockSlots.end_time
                                ? formattedData
                                : slot
                        );
                        return { ...prev, blockTime: updated };
                    } else {
                        return { ...prev, blockTime: [...updatedSlots, formattedData] };
                    }
                });
                setFormValue({ date: null, start_time: null, end_time: null });
                setErrors({});
                setShowModal();
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to check service!')

        }
        finally {
            setIsLoading(false)
        }
    };

    const handleConfirmation = () => {
        setOpeningHourseFormValue((prev) => {
            const updatedSlots = prev?.blockTime || [];
            const formattedData = {
                date: dayjs(formValue.date).format('DD/MM/YYYY'),
                start_time: dayjs(formValue.start_time).format('HH:mm'),
                end_time: dayjs(formValue.end_time).format('HH:mm'),
            };

            if (selectedBlockSlots) {
                const updated = updatedSlots.map((slot) =>
                    slot.date === selectedBlockSlots.date &&
                        slot.start_time === selectedBlockSlots.start_time &&
                        slot.end_time === selectedBlockSlots.end_time
                        ? formattedData
                        : slot
                );
                return { ...prev, blockTime: updated };
            } else {
                return { ...prev, blockTime: [...updatedSlots, formattedData] };
            }
        });
        setWarningModal(false)
        handleClose();
    }

    const handleClose = () => {
        setErrors({});
        setFormValue(initialFormValue)
        setShowModal(false)
        setSelectedBlockSlots(null)
        setIsLoading(false)
    }

    const closedDays = Object.keys(openingHourseFormValue?.bussiness_hours)
        .filter(day => openingHourseFormValue?.bussiness_hours[day].shop_open === "false" || openingHourseFormValue?.bussiness_hours[day].shop_open === false)
        .map(day => moment().day(day).day());  // Convert day names to day numbers

    const disablePastDates = (current) => {
        return current.isBefore(moment(), 'day') || closedDays.includes(current.day());
    };

    const parseTime = (timeStr) => {
        return timeStr === '24:00' ? dayjs('23:59', 'HH:mm') : dayjs(timeStr, 'HH:mm');
    };

    const getDisabledEndTime = (selectedDate, startTime, businessHours) => {
        const now = dayjs();
        const isTodaySelected = selectedDate && dayjs().isSame(selectedDate, 'day');

        const nowHour = now.hour();
        const nowMinute = now.minute();

        const startHour = startTime?.hour();
        const startMinute = startTime?.minute();

        const dayKey = selectedDate ? dayjs(selectedDate).format('dddd').toLowerCase() : null;
        const hoursToday = businessHours?.[dayKey];

        const openTime = hoursToday ? parseTime(hoursToday.open) : null;
        const closeTime = hoursToday ? parseTime(hoursToday.close) : null;

        return {
            disabledHours: () => {
                let hours = Array.from({ length: 24 }, (_, i) => i);

                if (openTime && closeTime) {
                    hours = hours.filter(i => i >= openTime.hour() && i <= closeTime.hour());
                }

                if (isTodaySelected) {
                    hours = hours.filter(i => i >= nowHour);
                }

                if (startTime) {
                    hours = hours.filter(i => i >= startHour);
                }

                return Array.from({ length: 24 }, (_, i) => i).filter(i => !hours.includes(i));
            },

            disabledMinutes: (selectedHour) => {
                let disabled = [];

                if (isTodaySelected && selectedHour === nowHour) {
                    disabled = Array.from({ length: nowMinute + 1 }, (_, i) => i);
                }

                if (startTime && selectedHour === startHour) {
                    disabled = Array.from({ length: startMinute + 1 }, (_, i) => i);
                }

                if (closeTime && selectedHour === closeTime.hour()) {
                    const closeMinute = closeTime.minute();
                    disabled = [
                        ...disabled,
                        ...Array.from({ length: 60 - closeMinute - 1 }, (_, i) => closeMinute + 1 + i)
                    ];
                }

                if (openTime && selectedHour === openTime.hour()) {
                    const openMinute = openTime.minute();
                    disabled = [
                        ...disabled,
                        ...Array.from({ length: openMinute }, (_, i) => i)
                    ];
                }

                return Array.from(new Set(disabled)).sort((a, b) => a - b);
            }
        };
    };

    const getDisabledStartTime = (selectedDate, businessHours) => {
        const now = dayjs();
        const isTodaySelected = selectedDate && dayjs().isSame(selectedDate, 'day');

        const currentHour = now.hour();
        const currentMinute = now.minute();

        const dayKey = selectedDate ? dayjs(selectedDate).format('dddd').toLowerCase() : null;
        const todayHours = businessHours?.[dayKey];

        let openTime = todayHours ? dayjs(todayHours.open, 'HH:mm') : null;
        let closeTime = todayHours ? dayjs(todayHours.close, 'HH:mm') : null;

        // Handle "24:00" by treating it as "23:59"
        if (todayHours?.close === "24:00") {
            closeTime = dayjs("23:58", "HH:mm");
        }

        return {
            disabledHours: () => {
                let allowedHours = Array.from({ length: 24 }, (_, i) => i);

                if (openTime && closeTime) {
                    allowedHours = allowedHours.filter(
                        i => i >= openTime.hour() && i <= closeTime.hour()
                    );
                }

                if (isTodaySelected) {
                    allowedHours = allowedHours.filter(i => i >= currentHour);
                }

                return Array.from({ length: 24 }, (_, i) => i).filter(i => !allowedHours.includes(i));
            },

            disabledMinutes: (selectedHour) => {
                let disabled = [];

                // Disable past minutes if today and selectedHour is currentHour
                if (isTodaySelected && selectedHour === currentHour) {
                    disabled = Array.from({ length: currentMinute + 1 }, (_, i) => i);
                }

                // Disable minutes before open minute
                if (openTime && selectedHour === openTime.hour()) {
                    disabled = [
                        ...disabled,
                        ...Array.from({ length: openTime.minute() }, (_, i) => i)
                    ];
                }

                // Disable minutes after close minute
                if (closeTime && selectedHour === closeTime.hour()) {
                    disabled = [
                        ...disabled,
                        ...Array.from({ length: 60 - closeTime.minute() - 1 }, (_, i) => closeTime.minute() + 1 + i)
                    ];
                }

                return Array.from(new Set(disabled)).sort((a, b) => a - b);
            }
        };
    };

    const handleCloseConfirmationModal = () => {
        setWarningModal(false)
    }

    const message = t('vendor.blockTimeConfirmationMessage')

    return (<>
        {isLoading &&
            <CustomSpinner />
        }
        <DeleteConfirmationModal
            showModal={warningModal}
            handleClose={handleCloseConfirmationModal}
            deleteOperation={handleConfirmation}
            message={message}
        />
        <Modal show={showModal} onHide={handleClose} dialogClassName="custom-modal customModal">
            <Modal.Header closeButton className="headerLine">
                <h5 className="mb-0">{t('calender.blockTime')}</h5>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('calender.date')}<span className="custom-astric">*</span></Form.Label>
                        <DatePicker
                            disabledDate={disablePastDates}
                            className="w-100"
                            format={'DD/MM/YYYY'}
                            value={formValue.date}
                            onChange={(date) => handleChange('date', date)}
                        />
                        <Small
                            name={'serviceLocation'}
                            err={errors.date}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('calender.startTime')}<span className="custom-astric">*</span></Form.Label>
                        <TimePicker
                            className="w-100"
                            format={format}
                            value={formValue.start_time}
                            onChange={(time) => handleChange('start_time', time)}
                            showNow={false}
                            minuteStep={1}
                            disabledHours={() =>
                                getDisabledStartTime(formValue.date, openingHourseFormValue?.bussiness_hours).disabledHours()
                            }
                            disabledMinutes={(hour) =>
                                getDisabledStartTime(formValue.date, openingHourseFormValue?.bussiness_hours).disabledMinutes(hour)
                            }
                        />
                        <Small
                            name={'serviceLocation'}
                            err={errors.start_time}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('calender.endTime')}<span className="custom-astric">*</span></Form.Label>
                        <TimePicker
                            className="w-100"
                            format={format}
                            value={formValue.end_time}
                            onChange={(time) => handleChange('end_time', time)}
                            showNow={false}
                            minuteStep={1}
                            disabledHours={() => getDisabledEndTime(formValue.date, formValue.start_time, openingHourseFormValue?.bussiness_hours).disabledHours()}
                            disabledMinutes={(hour) => getDisabledEndTime(formValue.date, formValue.start_time, openingHourseFormValue?.bussiness_hours).disabledMinutes(hour)}
                        />
                        <Small
                            name={'serviceLocation'}
                            err={errors.end_time}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-center footerLine">
                <Button className={`btn btn-light cancelbutton ${isLoading ? 'disable-btn' : ''}`} onClick={handleClose}>
                    {t('userBooking.cancel')}
                </Button>
                <Button className={`btn btn-light deletebutton ${isLoading ? 'disable-btn' : ''}`} onClick={handleSubmit}>
                    {isLoading ? <>{t('userBooking.saving')}</> : <>{t('calender.save')}</>}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    );
};

export default BlockTimeModal;